import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { getVisitorStats } from '../services/visitorTracking';
import { 
  FaArrowLeft, 
  FaFish, 
  FaHome, 
  FaSeedling, 
  FaShoppingCart, 
  FaMoneyBillWave,
  FaUsers,
  FaComments,
  FaChartLine,
  FaBox,
  FaCheckCircle,
  FaClock,
  FaTimes,
  FaTruck,
  FaSpinner,
  FaEye,
  FaUserPlus,
  FaUserCheck,
  FaClock as FaTime,
  FaGlobe,
  FaMobile,
  FaLaptop,
  FaTablet,
  FaMapMarkerAlt
} from 'react-icons/fa';

const StatsPage = () => {
  const [stats, setStats] = useState({
    products: {
      totalFish: 0,
      totalAquariums: 0,
      totalPlants: 0,
      availableFish: 0,
      soldFish: 0,
      availableAquariums: 0,
      availablePlants: 0
    },
    orders: {
      total: 0,
      pending: 0,
      confirmed: 0,
      shipped: 0,
      delivered: 0,
      cancelled: 0,
      totalRevenue: 0,
      averageOrderValue: 0
    },
    customers: {
      totalChats: 0,
      totalMessages: 0,
      activeChats: 0
    },
    popularProducts: [],
    visitors: {
      totalVisits: 0,
      uniqueVisitors: 0,
      newVisitors: 0,
      returningVisitors: 0,
      totalSessions: 0,
      avgSessionDuration: 0,
      visitsByDate: {},
      visitsByHour: {},
      visitsByDay: {},
      topPages: [],
      last30Days: [],
      last7Days: [],
      visitsByDevice: {},
      visitsByState: {},
      visitsByBrowser: {},
      visitsByOS: {},
      topStates: []
    }
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      setLoading(true);
      
      // Fetch products
      const [fishSnap, aquariumsSnap, plantsSnap] = await Promise.all([
        getDocs(collection(db, 'guppies')),
        getDocs(collection(db, 'aquariums')),
        getDocs(collection(db, 'plants'))
      ]);

      const fish = fishSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const aquariums = aquariumsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const plants = plantsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate product stats
      const availableFish = fish.filter(f => {
        if (f.singular) {
          return (f.malecount > 0 || f.femalecount > 0) && !f.sold_status;
        }
        return f.couplecount > 0 && !f.sold_status;
      }).length;

      const soldFish = fish.filter(f => f.sold_status).length;
      const availableAquariums = aquariums.length;
      const availablePlants = plants.length;

      // Fetch orders
      const ordersSnap = await getDocs(collection(db, 'orders'));
      const orders = ordersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate order stats
      const orderStats = {
        total: orders.length,
        pending: orders.filter(o => o.status === 'Pending').length,
        confirmed: orders.filter(o => o.status === 'Confirmed').length,
        shipped: orders.filter(o => o.status === 'Shipped').length,
        delivered: orders.filter(o => o.status === 'Delivered').length,
        cancelled: orders.filter(o => o.status === 'CancelledByClient' || o.status === 'Cancelled').length,
        totalRevenue: orders
          .filter(o => o.status === 'Confirmed' || o.status === 'Shipped' || o.status === 'Delivered')
          .reduce((sum, o) => sum + (o.totalPrice || 0), 0),
        averageOrderValue: 0
      };
      orderStats.averageOrderValue = orderStats.total > 0 
        ? orderStats.totalRevenue / (orderStats.confirmed + orderStats.shipped + orderStats.delivered || 1)
        : 0;

      // Calculate popular products
      const productCounts = {};
      orders.forEach(order => {
        if (order.items) {
          order.items.forEach(item => {
            const productName = item.guppyName || item.name || 'Unknown';
            const productType = item.type || 'guppy';
            const key = `${productType}_${productName}`;
            productCounts[key] = (productCounts[key] || 0) + (item.coupleCount || item.couplecount || item.quantity || 1);
          });
        }
      });

      const popularProducts = Object.entries(productCounts)
        .map(([key, count]) => {
          const [type, ...nameParts] = key.split('_');
          return {
            name: nameParts.join('_'),
            type: type,
            count: count
          };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // Fetch chats
      const chatsSnap = await getDocs(collection(db, 'chats'));
      const chats = chatsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Count messages in all chats
      let totalMessages = 0;
      let activeChats = 0;
      
      for (const chat of chats) {
        try {
          const messagesSnap = await getDocs(collection(db, 'chats', chat.id, 'messages'));
          const messageCount = messagesSnap.size;
          totalMessages += messageCount;
          if (messageCount > 0) activeChats++;
        } catch (error) {
          console.log(`Error fetching messages for chat ${chat.id}:`, error);
        }
      }

      // Fetch visitor stats with error handling
      let visitorStats;
      try {
        visitorStats = await getVisitorStats();
        // Ensure all required fields exist
        visitorStats = {
          totalVisits: visitorStats?.totalVisits || 0,
          uniqueVisitors: visitorStats?.uniqueVisitors || 0,
          newVisitors: visitorStats?.newVisitors || 0,
          returningVisitors: visitorStats?.returningVisitors || 0,
          totalSessions: visitorStats?.totalSessions || 0,
          avgSessionDuration: visitorStats?.avgSessionDuration || 0,
          visitsByDate: visitorStats?.visitsByDate || {},
          visitsByHour: visitorStats?.visitsByHour || {},
          visitsByDay: visitorStats?.visitsByDay || {},
          topPages: visitorStats?.topPages || [],
          last30Days: visitorStats?.last30Days || [],
          last7Days: visitorStats?.last7Days || [],
          visitsByDevice: visitorStats?.visitsByDevice || {},
          visitsByState: visitorStats?.visitsByState || {},
          visitsByBrowser: visitorStats?.visitsByBrowser || {},
          visitsByOS: visitorStats?.visitsByOS || {},
          topStates: visitorStats?.topStates || []
        };
      } catch (error) {
        console.error('Error fetching visitor stats:', error);
        // Use default values if error occurs
        visitorStats = {
          totalVisits: 0,
          uniqueVisitors: 0,
          newVisitors: 0,
          returningVisitors: 0,
          totalSessions: 0,
          avgSessionDuration: 0,
          visitsByDate: {},
          visitsByHour: {},
          visitsByDay: {},
          topPages: [],
          last30Days: [],
          last7Days: [],
          visitsByDevice: {},
          visitsByState: {},
          visitsByBrowser: {},
          visitsByOS: {},
          topStates: []
        };
      }

      setStats({
        products: {
          totalFish: fish.length,
          totalAquariums: aquariums.length,
          totalPlants: plants.length,
          availableFish,
          soldFish,
          availableAquariums,
          availablePlants
        },
        orders: orderStats,
        customers: {
          totalChats: chats.length,
          totalMessages,
          activeChats
        },
        popularProducts,
        visitors: visitorStats
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon, title, value, subtitle, color, gradient }) => (
    <div style={{
      background: gradient || `linear-gradient(135deg, ${color}15, ${color}05)`,
      border: `1px solid ${color}30`,
      borderRadius: '20px',
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = `0 20px 40px -10px ${color}40`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    >
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '100px',
        height: '100px',
        background: `radial-gradient(circle, ${color}20, transparent)`,
        borderRadius: '50%'
      }}></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', zIndex: 1 }}>
        <div style={{
          background: `linear-gradient(135deg, ${color}, ${color}dd)`,
          borderRadius: '12px',
          padding: '12px',
          color: 'white',
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '4px' }}>{title}</div>
          <div style={{ 
            color: color, 
            fontSize: '1.8rem', 
            fontWeight: 'bold',
            background: `linear-gradient(135deg, ${color}, ${color}aa)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            {value}
          </div>
          {subtitle && (
            <div style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '4px' }}>{subtitle}</div>
          )}
        </div>
      </div>
    </div>
  );

  const ProgressBar = ({ label, value, max, color }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>{label}</span>
          <span style={{ color: color, fontWeight: 'bold', fontSize: '0.9rem' }}>{value} / {max}</span>
        </div>
        <div style={{
          background: 'rgba(51, 65, 85, 0.5)',
          borderRadius: '10px',
          height: '12px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div style={{
            background: `linear-gradient(90deg, ${color}, ${color}dd)`,
            height: '100%',
            width: `${percentage}%`,
            borderRadius: '10px',
            transition: 'width 0.5s ease',
            boxShadow: `0 0 10px ${color}50`
          }}></div>
        </div>
      </div>
    );
  };

  const PieChart = ({ data, colors, size = 120 }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let currentAngle = -90;
    const radius = size / 2 - 10;
    const center = size / 2;

    const segments = data.map((item, index) => {
      const percentage = (item.value / total) * 100;
      const angle = (item.value / total) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;

      const x1 = center + radius * Math.cos((startAngle * Math.PI) / 180);
      const y1 = center + radius * Math.sin((startAngle * Math.PI) / 180);
      const x2 = center + radius * Math.cos((currentAngle * Math.PI) / 180);
      const y2 = center + radius * Math.sin((currentAngle * Math.PI) / 180);
      const largeArc = angle > 180 ? 1 : 0;

      return {
        path: `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`,
        color: colors[index],
        percentage: percentage.toFixed(1),
        label: item.label,
        value: item.value
      };
    });

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          {segments.map((segment, index) => (
            <g key={index}>
              <path
                d={segment.path}
                fill={segment.color}
                stroke="rgba(15, 23, 42, 0.8)"
                strokeWidth="2"
                style={{ transition: 'all 0.3s ease' }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.8';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                  e.target.style.transform = 'scale(1)';
                }}
              />
            </g>
          ))}
        </svg>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          {segments.map((segment, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                background: segment.color,
                borderRadius: '3px'
              }}></div>
              <span style={{ color: '#cbd5e1', fontSize: '0.85rem', flex: 1 }}>
                {segment.label}
              </span>
              <span style={{ color: segment.color, fontWeight: 'bold', fontSize: '0.85rem' }}>
                {segment.percentage}%
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px'
      }}>
        <FaSpinner style={{ 
          fontSize: '3rem', 
          color: '#10b981',
          animation: 'spin 2s linear infinite'
        }} />
        <div style={{ color: '#cbd5e1', fontSize: '1.2rem' }}>جاري تحميل الإحصائيات...</div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  const orderStatusData = [
    { label: 'قيد الانتظار', value: stats.orders.pending, color: '#fbbf24' },
    { label: 'مؤكد', value: stats.orders.confirmed, color: '#10b981' },
    { label: 'تم الشحن', value: stats.orders.shipped, color: '#3b82f6' },
    { label: 'تم التسليم', value: stats.orders.delivered, color: '#06b6d4' },
    { label: 'ملغي', value: stats.orders.cancelled, color: '#ef4444' }
  ].filter(item => item.value > 0);

  const productTypeData = [
    { label: 'أسماك', value: stats.products.totalFish, color: '#10b981' },
    { label: 'أحواض', value: stats.products.totalAquariums, color: '#3b82f6' },
    { label: 'نباتات', value: stats.products.totalPlants, color: '#06b6d4' }
  ].filter(item => item.value > 0);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
      position: 'relative',
      paddingBottom: '40px'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.05,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px'
      }}></div>

      {/* Header */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        padding: '24px 20px',
        background: 'rgba(30, 41, 59, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'rgba(16, 185, 129, 0.2)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '12px',
            padding: '12px',
            color: '#10b981',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.3)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <FaArrowLeft size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{
            margin: 0,
            fontSize: '1.8rem',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #10b981, #06b6d4)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            📊 لوحة الإحصائيات
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#94a3b8', fontSize: '0.9rem' }}>
            نظرة شاملة على أداء المتجر
          </p>
        </div>
      </div>

      {/* Content */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '32px 20px'
      }}>
        {/* Overview Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <StatCard
            icon={<FaShoppingCart />}
            title="إجمالي الطلبات"
            value={stats.orders.total}
            subtitle={`${stats.orders.confirmed} مؤكد`}
            color="#3b82f6"
          />
          <StatCard
            icon={<FaMoneyBillWave />}
            title="إجمالي الإيرادات"
            value={`${Math.round(stats.orders.totalRevenue).toLocaleString()} دج`}
            subtitle={`متوسط الطلب: ${Math.round(stats.orders.averageOrderValue).toLocaleString()} دج`}
            color="#10b981"
          />
          <StatCard
            icon={<FaBox />}
            title="إجمالي المنتجات"
            value={stats.products.totalFish + stats.products.totalAquariums + stats.products.totalPlants}
            subtitle={`${stats.products.totalFish} سمكة، ${stats.products.totalAquariums} حوض، ${stats.products.totalPlants} نبات`}
            color="#06b6d4"
          />
          <StatCard
            icon={<FaUsers />}
            title="العملاء"
            value={stats.customers.totalChats}
            subtitle={`${stats.customers.activeChats} محادثة نشطة`}
            color="#8b5cf6"
          />
        </div>

        {/* Product Statistics */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '20px',
          padding: '28px',
          marginBottom: '32px'
        }}>
          <h2 style={{
            color: '#cbd5e1',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FaFish style={{ color: '#10b981' }} />
            إحصائيات المنتجات
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '24px'
          }}>
            <StatCard
              icon={<FaFish />}
              title="الأسماك"
              value={stats.products.totalFish}
              subtitle={`${stats.products.availableFish} متوفرة، ${stats.products.soldFish} مباعة`}
              color="#10b981"
            />
            <StatCard
              icon={<FaHome />}
              title="أحواض السمك"
              value={stats.products.totalAquariums}
              subtitle={`${stats.products.availableAquariums} متوفرة`}
              color="#3b82f6"
            />
            <StatCard
              icon={<FaSeedling />}
              title="النباتات"
              value={stats.products.totalPlants}
              subtitle={`${stats.products.availablePlants} متوفرة`}
              color="#06b6d4"
            />
          </div>

          {/* Fish Availability Progress */}
          {stats.products.totalFish > 0 && (
            <div style={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '16px',
              padding: '20px',
              marginTop: '20px'
            }}>
              <h3 style={{ color: '#cbd5e1', fontSize: '1.1rem', marginBottom: '16px' }}>
                حالة مخزون الأسماك
              </h3>
              <ProgressBar
                label="متوفرة"
                value={stats.products.availableFish}
                max={stats.products.totalFish}
                color="#10b981"
              />
              <ProgressBar
                label="مباعة"
                value={stats.products.soldFish}
                max={stats.products.totalFish}
                color="#ef4444"
              />
            </div>
          )}
        </div>

        {/* Order Statistics */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '20px',
          padding: '28px',
          marginBottom: '32px'
        }}>
          <h2 style={{
            color: '#cbd5e1',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FaChartLine style={{ color: '#3b82f6' }} />
            إحصائيات الطلبات
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '16px',
            marginBottom: '32px'
          }}>
            <div style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <FaClock style={{ color: '#fbbf24', fontSize: '2rem', marginBottom: '8px' }} />
              <div style={{ color: '#fbbf24', fontSize: '1.8rem', fontWeight: 'bold' }}>
                {stats.orders.pending}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>قيد الانتظار</div>
            </div>
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <FaCheckCircle style={{ color: '#10b981', fontSize: '2rem', marginBottom: '8px' }} />
              <div style={{ color: '#10b981', fontSize: '1.8rem', fontWeight: 'bold' }}>
                {stats.orders.confirmed}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>مؤكد</div>
            </div>
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <FaTruck style={{ color: '#3b82f6', fontSize: '2rem', marginBottom: '8px' }} />
              <div style={{ color: '#3b82f6', fontSize: '1.8rem', fontWeight: 'bold' }}>
                {stats.orders.shipped}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>تم الشحن</div>
            </div>
            <div style={{
              background: 'rgba(6, 182, 212, 0.1)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <FaCheckCircle style={{ color: '#06b6d4', fontSize: '2rem', marginBottom: '8px' }} />
              <div style={{ color: '#06b6d4', fontSize: '1.8rem', fontWeight: 'bold' }}>
                {stats.orders.delivered}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>تم التسليم</div>
            </div>
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <FaTimes style={{ color: '#ef4444', fontSize: '2rem', marginBottom: '8px' }} />
              <div style={{ color: '#ef4444', fontSize: '1.8rem', fontWeight: 'bold' }}>
                {stats.orders.cancelled}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>ملغي</div>
            </div>
          </div>

          {/* Order Status Pie Chart */}
          {orderStatusData.length > 0 && (
            <div style={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '16px',
              padding: '24px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <PieChart
                data={orderStatusData}
                colors={orderStatusData.map(item => item.color)}
                size={200}
              />
            </div>
          )}
        </div>

        {/* Customer Engagement */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(139, 92, 246, 0.2)',
          borderRadius: '20px',
          padding: '28px',
          marginBottom: '32px'
        }}>
          <h2 style={{
            color: '#cbd5e1',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FaComments style={{ color: '#8b5cf6' }} />
            تفاعل العملاء
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            <StatCard
              icon={<FaUsers />}
              title="إجمالي المحادثات"
              value={stats.customers.totalChats}
              subtitle="محادثة"
              color="#8b5cf6"
            />
            <StatCard
              icon={<FaComments />}
              title="إجمالي الرسائل"
              value={stats.customers.totalMessages}
              subtitle="رسالة"
              color="#ec4899"
            />
            <StatCard
              icon={<FaCheckCircle />}
              title="المحادثات النشطة"
              value={stats.customers.activeChats}
              subtitle="محادثة نشطة"
              color="#10b981"
            />
          </div>
        </div>

        {/* Popular Products */}
        {stats.popularProducts.length > 0 && (
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(251, 191, 36, 0.2)',
            borderRadius: '20px',
            padding: '28px'
          }}>
            <h2 style={{
              color: '#cbd5e1',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <FaChartLine style={{ color: '#fbbf24' }} />
              المنتجات الأكثر مبيعاً
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {stats.popularProducts.map((product, index) => (
                <div
                  key={index}
                  style={{
                    background: 'rgba(15, 23, 42, 0.5)',
                    borderRadius: '12px',
                    padding: '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    border: '1px solid rgba(251, 191, 36, 0.2)',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(15, 23, 42, 0.8)';
                    e.currentTarget.style.transform = 'translateX(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(15, 23, 42, 0.5)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{
                    background: `linear-gradient(135deg, #fbbf24, #f59e0b)`,
                    borderRadius: '10px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.2rem'
                  }}>
                    {index + 1}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold', fontSize: '1rem' }}>
                      {product.name}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                      {product.type === 'guppy' ? '🐟 سمكة' : 
                       product.type === 'aquarium' ? '🏺 حوض' : 
                       product.type === 'plant' ? '🌿 نبات' : product.type}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(251, 191, 36, 0.2)',
                    borderRadius: '12px',
                    padding: '8px 16px',
                    color: '#fbbf24',
                    fontWeight: 'bold',
                    fontSize: '1.1rem'
                  }}>
                    {product.count} {product.type === 'guppy' ? 'زوج' : 'قطعة'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Visitor Analytics */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(6, 182, 212, 0.2)',
          borderRadius: '20px',
          padding: '28px',
          marginBottom: '32px'
        }}>
          <h2 style={{
            color: '#cbd5e1',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FaEye style={{ color: '#06b6d4' }} />
            إحصائيات الزوار
          </h2>

          {/* Visitor Overview Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '32px'
          }}>
            <StatCard
              icon={<FaEye />}
              title="إجمالي الزيارات"
              value={stats.visitors?.totalVisits || 0}
              subtitle="زيارة"
              color="#06b6d4"
            />
            <StatCard
              icon={<FaUsers />}
              title="زوار فريدون"
              value={stats.visitors?.uniqueVisitors || 0}
              subtitle="زائر"
              color="#10b981"
            />
            <StatCard
              icon={<FaUserPlus />}
              title="زوار جدد"
              value={stats.visitors?.newVisitors || 0}
              subtitle="زائر جديد"
              color="#3b82f6"
            />
            <StatCard
              icon={<FaUserCheck />}
              title="زوار عائدون"
              value={stats.visitors?.returningVisitors || 0}
              subtitle="زائر عائد"
              color="#8b5cf6"
            />
            <StatCard
              icon={<FaTime />}
              title="متوسط مدة الجلسة"
              value={(stats.visitors?.avgSessionDuration || 0) > 0 ? `${Math.round((stats.visitors.avgSessionDuration || 0) / 60)} دقيقة` : '0 دقيقة'}
              subtitle={`${stats.visitors?.totalSessions || 0} جلسة`}
              color="#f59e0b"
            />
          </div>

          {/* Daily Visitors Chart - Last 7 Days */}
          {stats.visitors.last7Days && stats.visitors.last7Days.length > 0 && (
            <div style={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#cbd5e1', fontSize: '1.2rem', marginBottom: '20px' }}>
                الزيارات اليومية - آخر 7 أيام
              </h3>
              <BarChart
                data={stats.visitors.last7Days}
                color="#06b6d4"
                height={200}
              />
            </div>
          )}

          {/* Peak Hours Chart */}
          {Object.keys(stats.visitors.visitsByHour).length > 0 && (
            <div style={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#cbd5e1', fontSize: '1.2rem', marginBottom: '20px' }}>
                ساعات الذروة
              </h3>
              <HourlyChart
                data={stats.visitors.visitsByHour}
                color="#10b981"
                height={200}
              />
            </div>
          )}

          {/* Peak Days Chart */}
          {Object.keys(stats.visitors.visitsByDay).length > 0 && (
            <div style={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#cbd5e1', fontSize: '1.2rem', marginBottom: '20px' }}>
                أيام الأسبوع الأكثر نشاطاً
              </h3>
              <DayChart
                data={stats.visitors.visitsByDay}
                color="#8b5cf6"
                height={200}
              />
            </div>
          )}

          {/* Top Pages */}
          {stats.visitors.topPages && stats.visitors.topPages.length > 0 && (
            <div style={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <h3 style={{ color: '#cbd5e1', fontSize: '1.2rem', marginBottom: '20px' }}>
                الصفحات الأكثر زيارة
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {stats.visitors.topPages.slice(0, 5).map((page, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'rgba(30, 41, 59, 0.5)',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      border: '1px solid rgba(6, 182, 212, 0.2)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(30, 41, 59, 0.8)';
                      e.currentTarget.style.transform = 'translateX(-4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(30, 41, 59, 0.5)';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <FaGlobe style={{ color: '#06b6d4', fontSize: '1.2rem' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#cbd5e1', fontWeight: 'bold', fontSize: '1rem' }}>
                        {page.path === '/' ? 'الصفحة الرئيسية' : page.path}
                      </div>
                    </div>
                    <div style={{
                      background: 'rgba(6, 182, 212, 0.2)',
                      borderRadius: '12px',
                      padding: '8px 16px',
                      color: '#06b6d4',
                      fontWeight: 'bold',
                      fontSize: '1rem'
                    }}>
                      {page.count} زيارة
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Device & Technology Analytics */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '20px',
          padding: '28px',
          marginBottom: '32px'
        }}>
          <h2 style={{
            color: '#cbd5e1',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FaMobile style={{ color: '#10b981' }} />
            الأجهزة والتقنيات
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px',
            marginBottom: '24px'
          }}>
            {/* Device Type Chart */}
            {Object.keys(stats.visitors.visitsByDevice || {}).length > 0 && (
              <div style={{
                background: 'rgba(15, 23, 42, 0.5)',
                borderRadius: '16px',
                padding: '24px'
              }}>
                <h3 style={{ color: '#cbd5e1', fontSize: '1.1rem', marginBottom: '20px' }}>
                  أنواع الأجهزة
                </h3>
                <DeviceChart
                  data={stats.visitors.visitsByDevice}
                  height={200}
                />
              </div>
            )}

            {/* Browser Chart */}
            {Object.keys(stats.visitors.visitsByBrowser || {}).length > 0 && (
              <div style={{
                background: 'rgba(15, 23, 42, 0.5)',
                borderRadius: '16px',
                padding: '24px'
              }}>
                <h3 style={{ color: '#cbd5e1', fontSize: '1.1rem', marginBottom: '20px' }}>
                  المتصفحات
                </h3>
                <BrowserChart
                  data={stats.visitors.visitsByBrowser}
                  height={200}
                />
              </div>
            )}

            {/* OS Chart */}
            {Object.keys(stats.visitors.visitsByOS || {}).length > 0 && (
              <div style={{
                background: 'rgba(15, 23, 42, 0.5)',
                borderRadius: '16px',
                padding: '24px'
              }}>
                <h3 style={{ color: '#cbd5e1', fontSize: '1.1rem', marginBottom: '20px' }}>
                  أنظمة التشغيل
                </h3>
                <OSChart
                  data={stats.visitors.visitsByOS}
                  height={200}
                />
              </div>
            )}
          </div>
        </div>

        {/* Geographic Analytics - Algerian States */}
        {stats.visitors.topStates && stats.visitors.topStates.length > 0 && (
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '20px',
            padding: '28px',
            marginBottom: '32px'
          }}>
            <h2 style={{
              color: '#cbd5e1',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <FaMapMarkerAlt style={{ color: '#3b82f6' }} />
              التوزيع الجغرافي - ولايات الجزائر
            </h2>

            {/* States Chart */}
            <div style={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#cbd5e1', fontSize: '1.2rem', marginBottom: '20px' }}>
                الولايات الأكثر زيارة
              </h3>
              <StateChart
                data={stats.visitors.topStates}
                color="#3b82f6"
                height={350}
              />
            </div>

            {/* Top States List */}
            <div style={{
              background: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <h3 style={{ color: '#cbd5e1', fontSize: '1.1rem', marginBottom: '20px' }}>
                قائمة الولايات
              </h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                gap: '12px' 
              }}>
                {stats.visitors.topStates.map((state, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'rgba(30, 41, 59, 0.5)',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(30, 41, 59, 0.8)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(30, 41, 59, 0.5)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      background: `linear-gradient(135deg, #3b82f6, #2563eb)`,
                      borderRadius: '8px',
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      flexShrink: 0
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        color: '#cbd5e1', 
                        fontWeight: 'bold', 
                        fontSize: '0.95rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {state.state}
                      </div>
                    </div>
                    <div style={{
                      background: 'rgba(59, 130, 246, 0.2)',
                      borderRadius: '8px',
                      padding: '6px 12px',
                      color: '#3b82f6',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      flexShrink: 0
                    }}>
                      {state.count}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Device Chart Component
const DeviceChart = ({ data, height = 200 }) => {
  const deviceColors = {
    mobile: '#10b981',
    desktop: '#3b82f6',
    tablet: '#8b5cf6'
  };
  const deviceIcons = {
    mobile: <FaMobile />,
    desktop: <FaLaptop />,
    tablet: <FaTablet />
  };
  const deviceNames = {
    mobile: 'جوال',
    desktop: 'سطح المكتب',
    tablet: 'تابلت'
  };

  const total = Object.values(data).reduce((sum, count) => sum + count, 0);
  const entries = Object.entries(data)
    .map(([device, count]) => ({
      device,
      count,
      percentage: ((count / total) * 100).toFixed(1),
      color: deviceColors[device] || '#64748b',
      icon: deviceIcons[device] || <FaMobile />,
      name: deviceNames[device] || device
    }))
    .sort((a, b) => b.count - a.count);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {entries.map((item, index) => (
        <div key={index} style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ color: item.color, fontSize: '1.2rem' }}>{item.icon}</div>
              <span style={{ color: '#cbd5e1', fontSize: '0.9rem', fontWeight: '600' }}>{item.name}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: item.color, fontWeight: 'bold', fontSize: '0.9rem' }}>{item.count}</span>
              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>({item.percentage}%)</span>
            </div>
          </div>
          <div style={{
            background: 'rgba(51, 65, 85, 0.5)',
            borderRadius: '10px',
            height: '12px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              background: `linear-gradient(90deg, ${item.color}, ${item.color}dd)`,
              height: '100%',
              width: `${item.percentage}%`,
              borderRadius: '10px',
              transition: 'width 0.5s ease',
              boxShadow: `0 0 10px ${item.color}50`
            }}></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Browser Chart Component
const BrowserChart = ({ data, height = 200 }) => {
  const browserColors = {
    Chrome: '#3b82f6',
    Firefox: '#f97316',
    Safari: '#10b981',
    Edge: '#06b6d4',
    Opera: '#ef4444',
    Other: '#64748b'
  };

  const total = Object.values(data).reduce((sum, count) => sum + count, 0);
  const entries = Object.entries(data)
    .map(([browser, count]) => ({
      browser,
      count,
      percentage: ((count / total) * 100).toFixed(1),
      color: browserColors[browser] || '#64748b'
    }))
    .sort((a, b) => b.count - a.count);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {entries.map((item, index) => (
        <div key={index} style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
            <span style={{ color: '#cbd5e1', fontSize: '0.9rem', fontWeight: '600' }}>{item.browser}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: item.color, fontWeight: 'bold', fontSize: '0.9rem' }}>{item.count}</span>
              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>({item.percentage}%)</span>
            </div>
          </div>
          <div style={{
            background: 'rgba(51, 65, 85, 0.5)',
            borderRadius: '10px',
            height: '12px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              background: `linear-gradient(90deg, ${item.color}, ${item.color}dd)`,
              height: '100%',
              width: `${item.percentage}%`,
              borderRadius: '10px',
              transition: 'width 0.5s ease',
              boxShadow: `0 0 10px ${item.color}50`
            }}></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// OS Chart Component
const OSChart = ({ data, height = 200 }) => {
  const osColors = {
    Windows: '#3b82f6',
    macOS: '#64748b',
    Linux: '#f59e0b',
    Android: '#10b981',
    iOS: '#000000',
    Other: '#64748b'
  };

  const total = Object.values(data).reduce((sum, count) => sum + count, 0);
  const entries = Object.entries(data)
    .map(([os, count]) => ({
      os,
      count,
      percentage: ((count / total) * 100).toFixed(1),
      color: osColors[os] || '#64748b'
    }))
    .sort((a, b) => b.count - a.count);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {entries.map((item, index) => (
        <div key={index} style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
            <span style={{ color: '#cbd5e1', fontSize: '0.9rem', fontWeight: '600' }}>{item.os}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: item.color, fontWeight: 'bold', fontSize: '0.9rem' }}>{item.count}</span>
              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>({item.percentage}%)</span>
            </div>
          </div>
          <div style={{
            background: 'rgba(51, 65, 85, 0.5)',
            borderRadius: '10px',
            height: '12px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              background: `linear-gradient(90deg, ${item.color}, ${item.color}dd)`,
              height: '100%',
              width: `${item.percentage}%`,
              borderRadius: '10px',
              transition: 'width 0.5s ease',
              boxShadow: `0 0 10px ${item.color}50`
            }}></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// State Chart Component (Algerian Wilayas)
const StateChart = ({ data, color, height = 350 }) => {
  const maxValue = Math.max(...data.map(d => d.count), 1);
  const top10 = data.slice(0, 10); // Show top 10 in chart

  return (
    <div style={{ position: 'relative', height: `${height}px`, display: 'flex', alignItems: 'flex-end', gap: '8px', overflowX: 'auto', paddingBottom: '10px' }}>
      {top10.map((item, index) => {
        const barHeight = (item.count / maxValue) * (height - 80);
        return (
          <div key={index} style={{ flex: 1, minWidth: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div style={{ position: 'relative', width: '100%', height: `${height - 80}px`, display: 'flex', alignItems: 'flex-end' }}>
              <div
                style={{
                  width: '100%',
                  height: `${barHeight}px`,
                  background: `linear-gradient(180deg, ${color}, ${color}dd)`,
                  borderRadius: '8px 8px 0 0',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  cursor: 'pointer',
                  boxShadow: `0 4px 12px ${color}40`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scaleY(1.05)';
                  e.currentTarget.style.boxShadow = `0 8px 20px ${color}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scaleY(1)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${color}40`;
                }}
                title={`${item.state}: ${item.count} زيارة`}
              >
                {item.count > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '-28px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(15, 23, 42, 0.95)',
                    color: color,
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    border: `1px solid ${color}40`,
                    zIndex: 10
                  }}>
                    {item.count}
                  </div>
                )}
              </div>
            </div>
            <div style={{ 
              color: '#94a3b8', 
              fontSize: '0.7rem', 
              textAlign: 'center', 
              height: '70px', 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              writingMode: 'horizontal-tb',
              wordBreak: 'break-word',
              maxWidth: '100%',
              lineHeight: '1.2'
            }}>
              {item.state.length > 8 ? item.state.substring(0, 8) + '...' : item.state}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Bar Chart Component for Daily Visitors
const BarChart = ({ data, color, height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d.visits), 1);
  const barWidth = 100 / data.length;

  return (
    <div style={{ position: 'relative', height: `${height}px`, display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
      {data.map((item, index) => {
        const barHeight = (item.visits / maxValue) * (height - 40);
        return (
          <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div style={{ position: 'relative', width: '100%', height: `${height - 40}px`, display: 'flex', alignItems: 'flex-end' }}>
              <div
                style={{
                  width: '100%',
                  height: `${barHeight}px`,
                  background: `linear-gradient(180deg, ${color}, ${color}dd)`,
                  borderRadius: '8px 8px 0 0',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  cursor: 'pointer',
                  boxShadow: `0 4px 12px ${color}40`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scaleY(1.05)';
                  e.currentTarget.style.boxShadow = `0 8px 20px ${color}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scaleY(1)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${color}40`;
                }}
                title={`${item.dayName}: ${item.visits} زيارة`}
              >
                {item.visits > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '-24px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(15, 23, 42, 0.9)',
                    color: color,
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    border: `1px solid ${color}40`
                  }}>
                    {item.visits}
                  </div>
                )}
              </div>
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.75rem', textAlign: 'center', height: '32px', display: 'flex', alignItems: 'center' }}>
              {item.dayName}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Hourly Chart Component
const HourlyChart = ({ data, color, height = 200 }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const maxValue = Math.max(...hours.map(h => data[h] || 0), 1);

  return (
    <div style={{ position: 'relative', height: `${height}px`, display: 'flex', alignItems: 'flex-end', gap: '4px', overflowX: 'auto' }}>
      {hours.map((hour) => {
        const visits = data[hour] || 0;
        const barHeight = (visits / maxValue) * (height - 60);
        return (
          <div key={hour} style={{ flex: 1, minWidth: '30px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <div style={{ position: 'relative', width: '100%', height: `${height - 60}px`, display: 'flex', alignItems: 'flex-end' }}>
              <div
                style={{
                  width: '100%',
                  height: `${barHeight}px`,
                  background: visits > 0 ? `linear-gradient(180deg, ${color}, ${color}dd)` : 'rgba(51, 65, 85, 0.3)',
                  borderRadius: '4px 4px 0 0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: visits > 0 ? `0 2px 8px ${color}30` : 'none'
                }}
                onMouseEnter={(e) => {
                  if (visits > 0) {
                    e.currentTarget.style.transform = 'scaleY(1.1)';
                    e.currentTarget.style.boxShadow = `0 4px 12px ${color}50`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scaleY(1)';
                  e.currentTarget.style.boxShadow = visits > 0 ? `0 2px 8px ${color}30` : 'none';
                }}
                title={`${hour}:00 - ${visits} زيارة`}
              >
                {visits > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '-20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(15, 23, 42, 0.9)',
                    color: color,
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    border: `1px solid ${color}40`
                  }}>
                    {visits}
                  </div>
                )}
              </div>
            </div>
            <div style={{ color: '#64748b', fontSize: '0.65rem', textAlign: 'center', writingMode: hour % 2 === 0 ? 'horizontal-tb' : 'horizontal-tb', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {hour % 2 === 0 ? hour : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Day Chart Component
const DayChart = ({ data, color, height = 200 }) => {
  const dayNames = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
  const maxValue = Math.max(...dayNames.map(d => data[d] || 0), 1);

  return (
    <div style={{ position: 'relative', height: `${height}px`, display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
      {dayNames.map((dayName) => {
        const visits = data[dayName] || 0;
        const barHeight = (visits / maxValue) * (height - 40);
        return (
          <div key={dayName} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div style={{ position: 'relative', width: '100%', height: `${height - 40}px`, display: 'flex', alignItems: 'flex-end' }}>
              <div
                style={{
                  width: '100%',
                  height: `${barHeight}px`,
                  background: `linear-gradient(180deg, ${color}, ${color}dd)`,
                  borderRadius: '8px 8px 0 0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: `0 4px 12px ${color}40`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scaleY(1.05)';
                  e.currentTarget.style.boxShadow = `0 8px 20px ${color}60`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scaleY(1)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${color}40`;
                }}
                title={`${dayName}: ${visits} زيارة`}
              >
                {visits > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '-24px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(15, 23, 42, 0.9)',
                    color: color,
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    border: `1px solid ${color}40`
                  }}>
                    {visits}
                  </div>
                )}
              </div>
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', height: '32px', display: 'flex', alignItems: 'center' }}>
              {dayName}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatsPage;

